import React, { useContext } from 'react';
import { Button } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from 'src/context';
import { useCreateBitacoraComment } from 'src/graphql/bitacora';
import { getCssColors } from 'src/scss/theme';
import { Roles } from 'src/utils/roles';
import moment from 'moment';
import { importAlertState } from 'src/redux/actions/alert';
import { renderBitacoraCommentWithMentions } from './renderMarkdown';
import BitacoraEditor from 'src/modules/bitacora/BitacoraEditor';
import aiIcon from 'src/assets/icons/ai-icon.svg';
import { useCompanyMentionUsers } from 'src/hooks/useCompanyMentionUsers';

const isEmptyText = (text) => {
  if (!text || typeof text !== 'string') return true;
  const stripped = text
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped === '';
};

/** Iniciales: nombre+apellido (JV), o dos primeras letras del nombre si no hay apellido; fallback email. */
const getCommentInitials = (name, email) => {
  if (name && typeof name === 'string') {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 2) return parts[0].substring(0, 2).toUpperCase();
    if (parts.length === 1 && parts[0].length === 1) return parts[0].toUpperCase();
  }
  if (email && typeof email === 'string') {
    const prefix = (email.split('@')[0] || '').trim();
    const dotParts = prefix.split('.');
    if (dotParts.length >= 2) return (dotParts[0][0] + dotParts[dotParts.length - 1][0]).toUpperCase();
    return prefix.substring(0, 2).toUpperCase() || 'U';
  }
  return 'U';
};

const getCommentAuthorLabel = (comment) => {
  if (comment?.source === 'ai') return 'Análisis de IA';
  if (comment?.source === 'email') return 'Correo enviado al cliente';
  return comment?.user?.email || comment?.user?.name || 'Usuario';
};

const BitacoraCommentsSection = ({ bitacora, companyId, onCommentCreated }) => {
  const dispatch = useDispatch();
  const userInfo = useContext(UserContext);
  const currentUser = useSelector((state) => state.auth);
  const { teal } = getCssColors();
  const roles = userInfo?.roles ?? currentUser?.roles ?? [];
  const canViewInternalAiComments =
    roles?.includes(Roles.ADMIN) ||
    roles?.includes(Roles.SUPER_COLABORADOR) ||
    roles?.includes(Roles.COLABORADOR);

  const [newNote, setNewNote] = React.useState('');
  const [showEditor, setShowEditor] = React.useState(false);

  const createComment = useCreateBitacoraComment();
  const effectiveCompanyId = companyId || bitacora?.companyId;
  const userId = currentUser?.uid;
  const mentionUsers = useCompanyMentionUsers(effectiveCompanyId, userId);

  const allComments = bitacora?.comments || [];
  // "Correo enviado al cliente" se muestra en su sección dedicada, no en historial general.
  const comments = allComments.filter((comment) => {
    if (comment?.source === 'email') return false;
    if (!canViewInternalAiComments && comment?.source === 'ai') return false;
    return true;
  });

  const addNote = async () => {
    if (isEmptyText(newNote)) return;
    const input = { comment: newNote };
    try {
      const { error } = await createComment.executeMutation(bitacora?.id, input);
      if (error) throw error;
      setNewNote('');
      setShowEditor(false);
      onCommentCreated?.();
    } catch (e) {
      const message = e?.message || e?.graphQLErrors?.[0]?.message || 'No se pudo publicar el comentario';
      dispatch(importAlertState({ content: message, state: 'error', duration: 5 }));
    }
  };

  return (
    <div className="bitacora-comments-section">
      <div className="bitacora-detail-section-title">Historial de comentarios</div>
      <div className="bitacora-comments-list">
        {comments.length > 0 ? (
          comments.slice(0).reverse().map((c, i) => (
            <div key={i} className={`bitacora-comments-item${c.source === 'ai' ? ' bitacora-comments-item--ai' : ''}`}>
              <div className="bitacora-comments-avatar">
                {c.source === 'ai' ? (
                  <img src={aiIcon} alt="" className="bitacora-comments-avatar-ai-icon" aria-hidden />
                ) : (
                  <span className="bitacora-comments-avatar-initials">
                    {getCommentInitials(c.user?.name, c.user?.email)}
                  </span>
                )}
              </div>
              <div className="bitacora-comments-body">
                <div className="bitacora-comments-header">
                  <span>{getCommentAuthorLabel(c)}</span>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>{moment(c.date).fromNow()}</span>
                </div>
                <div
                  className="bitacora-comments-text bitacora-comments-text--markdown bitacora-editor-content"
                  dangerouslySetInnerHTML={{ __html: renderBitacoraCommentWithMentions(c.comment, { backgroundColor: teal }) }}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="bitacora-comments-empty">No hay comentarios aún.</div>
        )}
      </div>
      {!showEditor ? (
        <div className="bitacora-comments-add-row">
          <div className="bitacora-comments-avatar bitacora-comments-avatar--spacer" aria-hidden />
          <div className="bitacora-comments-add-wrap">
            <button
              type="button"
              className="bitacora-comments-add-btn"
              onClick={() => setShowEditor(true)}
            >
              Agregar comentario
            </button>
          </div>
        </div>
      ) : (
        <div className="bitacora-comments-input-row">
          <div className="bitacora-comments-avatar">
            <span className="bitacora-comments-avatar-initials">
              {getCommentInitials(userInfo?.name, userInfo?.email)}
            </span>
          </div>
          <div className="bitacora-comments-box">
            <div className="bitacora-comments-editor-wrap">
              <BitacoraEditor
                contenidoHtml={newNote}
                onChange={setNewNote}
                placeholder="Escribe un comentario… Usa @ para etiquetar a un usuario"
                mentionUsers={mentionUsers}
              />
            </div>
            <div className="bitacora-comments-actions">
              <Button
                size="small"
                onClick={addNote}
                className="bitacora-comments-send-btn"
                loading={createComment.fetching}
                disabled={isEmptyText(newNote)}
              >
                Enviar comentario
              </Button>
              <button
                type="button"
                className="bitacora-comments-cancel-btn"
                onClick={() => { setShowEditor(false); setNewNote(''); }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BitacoraCommentsSection;
